// Contractor Visits admin screens, piece 6. Pure: data in, HTML out.
// Words: OBSERVATIONS_SCAN_WORDS.md rev 3, "Admin: Contractor Visits". Mockup: MOCKUP_CONTRACTOR_ACCESS.html rev 3.

import { startedTextOf } from "./work-started";
import {
  addDays,
  clockText,
  dayLineOf,
  unseenYes,
  usShort,
  visitRowsFor,
  visitStatusOf,
  whoLine,
  type VisitRow,
  type VisitStatus,
} from "./contractor";
import { esc, ident } from "./training-screens";

function row(label: string, value: string): string {
  return `<div class="r"><div class="k">${esc(label)}</div><div class="v">${value}</div><div class="e"></div></div>`;
}

export function statusPill(st: VisitStatus): string {
  const tone = st === "Waiting" ? "amber" : st === "Not Mine" || st === "Expired" ? "red" : "grey";
  return `<span class="pill ${tone}">${esc(st)}</span>`;
}

export function visitList(all: VisitRow[], siteNames: Record<string, string>, site: string, search: string, today: string, nowIso: string, flash = ""): string {
  const shown = visitRowsFor(all, site, search, nowIso);
  const sites = Object.keys(siteNames).sort();
  const options = [
    `<option value="all"${site === "all" ? " selected" : ""}>All Sites</option>`,
    ...sites.map((x) => `<option value="${esc(x)}"${site === x ? " selected" : ""}>${esc(`${x} · ${siteNames[x]}`)}</option>`),
  ].join("");
  const rows = shown
    .map((v) => {
      const st = visitStatusOf(v, nowIso);
      const sponsor = v.sponsorName ? `Sponsor ${v.sponsorName}` : `Sponsor ${v.sponsorEmail}`;
      const when = st === "Waiting" ? `sent ${clockText(v.createdAt)}` : dayLineOf(v, today) || startedTextOf(v.createdAt);
      const yes = unseenYes(v).length;
      return `<a class="ar gl-row em-row" href="#/contractors/r/${encodeURIComponent(v.visitId)}">
        <span class="chip dark">${esc(v.visitId)}</span>
        <span class="t"><b>${esc(`${v.contractor.company} · ${v.contractor.name}`)}</b><small>${esc([sponsor, when].filter(Boolean).join(" · "))}</small>
        <span class="wr-pills">${statusPill(st)}${yes ? `<span class="pill red">Yes to see</span>` : ""}</span></span></a>`;
    })
    .join("");
  const title = site === "all" ? "All Sites" : siteNames[site] ?? `Site ${site}`;
  const onSite = all.filter((v) => (site === "all" || v.siteNumber === site) && visitStatusOf(v, nowIso) === "On Site").length;
  return `${ident("CV", `Contractor Visits · ${title}`, `${onSite} on site · orientation, sponsor and daily checks`)}
    ${flash}
    <div class="em-ctrl">
      <label class="site-pick" for="cv-site">Site <select id="cv-site" data-act="cv-site">${options}</select></label>
      <label class="search" style="flex:1 1 220px"><span class="sr">Search</span><input id="cv-search" class="fin wide" type="search" value="${esc(search)}" placeholder="Search company or name" autocomplete="off"></label>
    </div>
    <div class="box" id="cv-rows">${rows || `<div class="empty">${search.trim() ? `Nothing matches "${esc(search.trim())}".` : "No contractor visits here yet."}</div>`}</div>
    <p class="ro">Waiting and On Site first. A Yes on a daily check stays amber until Mark Seen.</p>`;
}

/** One line per job day: No, Yes with the line, No answer, Waiting, or Not sent. */
export function dayRows(v: VisitRow, today: string): { label: string; value: string; yes: boolean }[] {
  if (!v.startOn) return [];
  const out: { label: string; value: string; yes: boolean }[] = [];
  for (let n = 1; n <= v.jobDays; n++) {
    const on = addDays(v.startOn, n - 1);
    const f = v.followUps.find((x) => x.day === n);
    let value: string;
    if (f?.answer === "no") value = `No · ${f.at ? clockText(f.at) : ""}`;
    else if (f?.answer === "yes") value = `Yes · ${f.note}${f.at ? ` · ${clockText(f.at)}` : ""}`;
    else if (f) value = on < today ? "No answer" : "Waiting";
    else value = on >= today && v.status === "On Site" ? "Not sent yet" : "Not sent";
    out.push({ label: `Day ${n} · ${usShort(on)}`, value, yes: f?.answer === "yes" });
  }
  return out;
}

export interface VisitScreen {
  busy: boolean;
  error: string;
}

export function visitRecord(v: VisitRow, siteName: string, backHref: string, today: string, nowIso: string, st: VisitScreen, flash = ""): string {
  const status = visitStatusOf(v, nowIso);
  const yes = unseenYes(v);
  const sub = [v.contractor.company, siteName, status === "On Site" ? `On Site, ${dayLineOf(v, today)}` : status].filter(Boolean).join(" · ");
  const sponsorLine = v.confirmedAt
    ? `${esc(`${v.sponsorName} · ${v.sponsorEmail}`)}<span class="chg-line">Confirmed ${esc(startedTextOf(v.confirmedAt))} · all four commitments ticked</span>`
    : status === "Not Mine"
      ? `${esc(v.sponsorEmail)}<span class="chg-line">Not Mine${v.sponsorName ? ` · ${esc(v.sponsorName)}` : ""}${v.endedAt ? ` · ${esc(startedTextOf(v.endedAt))}` : ""}</span>`
      : `${esc(v.sponsorEmail)}<span class="chg-line">${status === "Expired" ? "No answer in 4 hours" : `Waiting · expires ${esc(clockText(v.expiresAt))}`}</span>`;
  const days = dayRows(v, today)
    .map((d) => row(d.label, d.yes ? `<span class="gl-text-bad">${esc(d.value)}</span>` : esc(d.value)))
    .join("");
  const seen = yes.length
    ? `<div class="note red">${yes.length === 1 ? "A Yes answer" : `${yes.length} Yes answers`} not seen yet.</div>
      ${st.error ? `<div class="note red" role="status">${esc(st.error)}</div>` : ""}
      <button class="prim" type="button" data-act="cv-seen" data-id="${esc(v.visitId)}"${st.busy ? " disabled" : ""}>${st.busy ? "Saving…" : "Mark Seen"}</button>`
    : "";
  return `<a class="back" href="${esc(backHref)}">Contractor Visits</a>
    ${ident("CV", v.visitId, sub)}
    ${flash}
    ${seen}
    <div class="box"><div class="boxhd"><div class="nm"><b>The Contractor</b><small>Program ${esc(v.check.version)} · check passed 5 of 5${v.check.misses ? `, ${v.check.misses} ${v.check.misses === 1 ? "miss" : "misses"}` : ""}</small></div>${statusPill(status)}</div><div class="rows">
      ${row("Name", esc(`${whoLine(v)} · ${v.contractor.phone}`))}
      ${row("Their supervisor", esc(v.contractor.supervisor))}
      ${row("Sponsor", sponsorLine)}
      ${row("Job", esc(v.startOn ? `${v.jobDays} ${v.jobDays === 1 ? "day" : "days"}, ${usShort(v.startOn)} to ${usShort(v.endsOn)}` : `${v.jobDays} ${v.jobDays === 1 ? "day" : "days"} asked`))}
      ${row("Check passed", esc(startedTextOf(v.check.passedAt)))}
      ${row("Label scanned", esc(v.scannedAssetId))}
      ${row("Sent", esc(startedTextOf(v.createdAt)))}
      ${v.endReason ? row("Ended", esc(`${v.endReason}${v.endedAt ? ` · ${startedTextOf(v.endedAt)}` : ""}`)) : ""}
    </div></div>
    ${v.startOn ? `<div class="box"><div class="boxhd"><div class="nm"><b>Daily Checks</b><small>One email to the sponsor each job day at 3 PM</small></div></div><div class="rows">${days}</div></div>` : ""}
    <p class="ro">Kept on record. The sponsor's links are stored only as codes that cannot be read back.</p>`;
}
